import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useSearchParams } from "react-router-dom";
import {
  RealtimeKitProvider,
  useRealtimeKitClient,
  useRealtimeKitMeeting,
} from "@cloudflare/realtimekit-react";
import { RtkMeeting } from "@cloudflare/realtimekit-react-ui";

interface MeetingProduct {
  _id: string;
  name?: string;
  realtimeKit?: {
    meetingId?: string;
    appId?: string;
    status?: string;
  };
}

function MeetingUi() {
  const { meeting } = useRealtimeKitMeeting();

  if (!meeting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-black">
      <RtkMeeting meeting={meeting} mode="fill" showSetupScreen />
    </div>
  );
}

function MeetingExperience({ authToken }: { authToken: string }) {
  const [meetingClient, initMeeting] = useRealtimeKitClient();

  useEffect(() => {
    initMeeting({ authToken });
  }, [authToken, initMeeting]);

  return (
    <RealtimeKitProvider value={meetingClient}>
      <MeetingUi />
    </RealtimeKitProvider>
  );
}

export function MeetingRoute() {
  const { id: productId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const authTokenFromQuery = searchParams.get("authToken");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productName, setProductName] = useState("");
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);

  useEffect(() => {
    if (!productId) return;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setError(null);
      setAuthToken(null);
      setMeetingId(null);

      try {
        const productRes = await fetch(`/api/products/${productId}`);
        if (!productRes.ok) {
          if (productRes.status === 404) setNotFound(true);
          else setError("Failed to load meeting");
          return;
        }

        const productData = (await productRes.json()) as { product?: MeetingProduct };
        const product = productData.product;

        if (!product?.realtimeKit?.meetingId) {
          setError("Meeting is not configured for this product");
          return;
        }

        setProductName(product.name?.trim() ?? "");
        setMeetingId(product.realtimeKit.meetingId);

        if (authTokenFromQuery) {
          setAuthToken(authTokenFromQuery);
          return;
        }

        const joinRes = await fetch(`/api/products/${productId}/meeting/join`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });

        const joinData = (await joinRes.json()) as { authToken?: string; error?: string };

        if (!joinRes.ok || !joinData.authToken) {
          setError(joinData.error ?? "Failed to join meeting");
          return;
        }

        setAuthToken(joinData.authToken);
      } catch (e) {
        console.error("Meeting load error:", e);
        setError("Failed to load meeting");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [productId, authTokenFromQuery]);

  const pageTitle = productName.trim() ? `${productName.trim()} · Meeting` : "Meeting";

  if (!productId) {
    return (
      <>
        <Helmet>
          <title>Meeting</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          Missing meeting ID
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          Loading...
        </div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <Helmet>
          <title>Meeting not found</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          Meeting not found
        </div>
      </>
    );
  }

  if (error || !authToken || !meetingId) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white px-4 text-center">
          {error ?? "Unable to start meeting"}
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <MeetingExperience authToken={authToken} />
    </>
  );
}
